import React from "react";
import styled from "styled-components";

import {
  brand_orange,
  brand_orange_deep,
  brand_yellow,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import SearchableSelect from "../../components/SearchableSelect";
import SearchBox from "../../components/SearchBox";

// Presentational filter row for /video-pembelajaran. All state lives in
// useVideoPembelajaranIndex; this only renders controls and calls `update`.
//
// Only the free-text box is debounced (useDebouncedSearch); every other control
// here is a single deliberate action and applies immediately.
//
// The Nama Materi Belajar filter is NOT here — it is the sidebar (MateriSidebar),
// since group navigation is the primary way to browse this catalog.

const SORT_OPTIONS = [
  { value: "newest", label: "Terbaru" },
  { value: "oldest", label: "Terlama" },
  { value: "name_asc", label: "Nama A-Z" },
];

const Panel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fbfbfc;

  [data-theme="dark"] & {
    background: #14141b;
    border-color: #2b2b36;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 170px;
  flex: ${(props) => (props.$grow ? "1 1 240px" : "0 0 auto")};

  @media ${narrowScreenMediaQuery} {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;

  [data-theme="dark"] & {
    background: #1f2937;
    border-color: #374151;
  }
`;

const SegButton = styled.button`
  padding: 7px 12px;
  border: 0;
  border-radius: 7px;
  background: ${(props) => (props.$active ? brand_orange_deep : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : "#4b5563")};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 800 : 600)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 1px;
  }

  [data-theme="dark"] & {
    color: ${(props) => (props.$active ? "#1f2937" : "#d1d5db")};
    background: ${(props) => (props.$active ? brand_yellow : "transparent")};
  }
`;

const ResetButton = styled.button`
  margin-left: auto;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 1px;
  }

  [data-theme="dark"] & {
    background: #1f2937;
    border-color: #374151;
    color: #d1d5db;
  }

  @media ${narrowScreenMediaQuery} {
    margin-left: 0;
    flex: 1 1 100%;
  }
`;

function toSelectOptions(options) {
  return options.map((o) => ({
    value: o.slug,
    label: o.name,
    count: o.count,
  }));
}

function FilterBar({
  filters,
  update,
  reset,
  activeFilterCount,
  signedIn,
  categoryOptions = [],
  // Search is delegated to <SearchBox>, which keeps the draft in its own state so
  // typing does not re-render this panel, the sidebar or the results grid.
  // `commitSearch` must be referentially stable.
  commitSearch,
  searchResetToken,
  loading = false,
}) {
  const categorySelectOptions = React.useMemo(
    () => toSelectOptions(categoryOptions),
    [categoryOptions],
  );

  return (
    <Panel>
      <Field $grow>
        <FieldLabel htmlFor="vp-search">Cari video</FieldLabel>
        <SearchBox
          id="vp-search"
          initialValue={filters.q}
          onCommit={commitSearch}
          resetToken={searchResetToken}
          placeholder="Judul, kategori, atau nama materi..."
          ariaLabel="Cari video pembelajaran"
          loading={loading}
        />
      </Field>

      {categorySelectOptions.length > 0 && (
        <Field>
          <FieldLabel htmlFor="vp-category">Kategori</FieldLabel>
          <SearchableSelect
            id="vp-category"
            value={filters.category}
            onChange={(next) => update({ category: next })}
            options={categorySelectOptions}
            allLabel="Semua Kategori"
            ariaLabel="Filter kategori"
            placeholder="Semua Kategori"
            searchPlaceholder="Cari kategori..."
          />
        </Field>
      )}

      <Field>
        <FieldLabel htmlFor="vp-sort">Urutkan</FieldLabel>
        <SearchableSelect
          id="vp-sort"
          value={filters.sort}
          onChange={(next) => update({ sort: next })}
          options={SORT_OPTIONS}
          showAll={false}
          ariaLabel="Urutkan video"
        />
      </Field>

      <Field>
        <FieldLabel as="span">Akses</FieldLabel>
        <Segmented role="group" aria-label="Filter akses">
          <SegButton
            type="button"
            $active={!filters.access}
            onClick={() => update({ access: "" })}
          >
            Semua
          </SegButton>
          <SegButton
            type="button"
            $active={filters.access === "free"}
            onClick={() => update({ access: "free" })}
          >
            Gratis
          </SegButton>
          <SegButton
            type="button"
            $active={filters.access === "subscription"}
            onClick={() => update({ access: "subscription" })}
          >
            Langganan
          </SegButton>
        </Segmented>
      </Field>

      {/* Watched state is per-user, so the server treats it as a no-op for an
       * anonymous visitor. Hide it rather than offering a control that does
       * nothing.
       */}
      {signedIn && (
        <Field>
          <FieldLabel as="span">Progres</FieldLabel>
          <Segmented role="group" aria-label="Filter progres menonton">
            <SegButton
              type="button"
              $active={!filters.watched}
              onClick={() => update({ watched: "" })}
            >
              Semua
            </SegButton>
            <SegButton
              type="button"
              $active={filters.watched === "unwatched"}
              onClick={() => update({ watched: "unwatched" })}
            >
              Belum
            </SegButton>
            <SegButton
              type="button"
              $active={filters.watched === "in_progress"}
              onClick={() => update({ watched: "in_progress" })}
            >
              Berjalan
            </SegButton>
            <SegButton
              type="button"
              $active={filters.watched === "completed"}
              onClick={() => update({ watched: "completed" })}
            >
              Selesai
            </SegButton>
          </Segmented>
        </Field>
      )}

      <ResetButton
        type="button"
        onClick={reset}
        disabled={activeFilterCount === 0}
      >
        Reset
        {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </ResetButton>
    </Panel>
  );
}

// Memoized so results landing does not re-render the whole control panel.
export default React.memo(FilterBar);
